import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToActionQueue } from "@/utils/mistralInsightMappers";
import {
  Users,
  TrendingUp,
  School,
  Award,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  BarChart3,
  Medal,
} from "lucide-react";
import {
  buildTeacherTqiGaugeStyle,
  buildTeacherHeroHeadline,
  buildTeacherHeroNarrative,
  buildTeacherStatusChips,
  buildTeacherPrimaryAction,
  buildTeacherExecutiveKpis,
  buildTeacherInsightStrip,
  buildStaffingDonut,
  buildBlockPairedRows,
  buildGrowthRows,
  buildExecutiveActionQueue,
  buildBlockWatchlist,
  buildNextReviewNote,
  buildTeacherExecutiveMarkdown,
} from "@/utils/teacherDerived";
import { toTitleCase } from "../utils/toTitleCase";

const KPI_ICONS = {
  green: Users,
  teal: TrendingUp,
  purple: School,
  amber: AlertTriangle,
};

const ACTION_ICONS = {
  purple: Medal,
  red: AlertTriangle,
  green: TrendingUp,
};

const STRIP_ICONS = {
  blue: BarChart3,
  green: BarChart3,
  purple: BarChart3,
  amber: BarChart3,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TeacherExecutiveTab = ({
  overview = {},
  schoolDistribution = [],
  blockComparison = [],
  blockData = [],
  onNavigateTab,
}) => {
  const tqi = overview.teacher_quality_index ?? 0;
  const hasData = Boolean(overview?.teachers_cy);

  const buildLocalMarkdown = useCallback(
    () => buildTeacherExecutiveMarkdown(overview, blockData),
    [overview, blockData]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ t: overview?.teachers_cy, q: overview?.teacher_quality_index }),
    [overview?.teachers_cy, overview?.teacher_quality_index]
  );
  const {
    loading: aiLoading,
    pickHeadline,
    pickFromBucket,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "teacher-executive",
    title: "Teacher Executive Overview",
    buildLocalMarkdown,
    context: { overview, blockData },
    enabled: hasData,
    scopeKey,
  });

  const gaugeStyle = useMemo(() => buildTeacherTqiGaugeStyle(tqi), [tqi]);
  const localHeadline = useMemo(() => buildTeacherHeroHeadline(overview), [overview]);
  const localNarrative = useMemo(() => buildTeacherHeroNarrative(overview), [overview]);
  const headline = useMemo(
    () => (aiLoading ? localHeadline : pickHeadline(localHeadline)),
    [aiLoading, pickHeadline, localHeadline]
  );
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 1)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const statusChips = useMemo(() => {
    const chips = buildTeacherStatusChips(overview);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [overview, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(
    () => buildTeacherPrimaryAction(overview, blockData),
    [overview, blockData]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const kpis = useMemo(() => buildTeacherExecutiveKpis(overview), [overview]);
  const insightStrip = useMemo(
    () => buildTeacherInsightStrip(overview, schoolDistribution),
    [overview, schoolDistribution]
  );
  const donut = useMemo(
    () => buildStaffingDonut(schoolDistribution, overview.total_schools),
    [schoolDistribution, overview.total_schools]
  );
  const pairedRows = useMemo(() => buildBlockPairedRows(blockComparison), [blockComparison]);
  const growthRows = useMemo(() => buildGrowthRows(blockData), [blockData]);
  const localActionQueue = useMemo(
    () => buildExecutiveActionQueue(overview, blockData),
    [overview, blockData]
  );
  const actionQueue = useMemo(
    () => pickMapped(localActionQueue, "recs", bulletsToActionQueue),
    [pickMapped, localActionQueue]
  );
  const watchlist = useMemo(() => buildBlockWatchlist(blockData), [blockData]);
  const localNextReview = useMemo(() => buildNextReviewNote(), []);
  const nextReview = useMemo(
    () => (aiLoading ? localNextReview : pickFromBucket(localNextReview, "actions", 0)),
    [aiLoading, pickFromBucket, localNextReview]
  );

  return (
    <div className="tex-exec-tab" data-testid="teacher-executive">
      <section className="tex-hero-card" id="tex-exec-hero">
        <div className="tex-hero-layout">
          <div>
            <div className="tex-health-main">
              <div
                className="tex-gauge"
                style={gaugeStyle}
                aria-label={`Teacher quality index ${tqi}`}
              >
                <div>
                  <strong>{tqi}</strong>
                  <span>TQI Score</span>
                </div>
              </div>
              <div className="tex-health-copy">
                <h2>{toTitleCase(headline)}</h2>
                <p>{narrative}</p>
                <div className="tex-status-row">
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`tex-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="tex-summary-note">
              <div className="tex-icon-box purple">
                <ShieldCheck size={22} />
              </div>
              <div>
                <b>Recommended executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="tex-metric-grid" aria-label="Teacher executive KPIs">
            {kpis.map((card) => {
              const Icon = KPI_ICONS[card.iconClass] ?? Users;
              return (
                <article key={card.label} className={`tex-metric-card ${card.cardClass}`.trim()}>
                  <div className={`tex-icon-box ${card.iconClass}`.trim()}>
                    <Icon size={20} />
                  </div>
                  <small>{card.label}</small>
                  <strong>{card.value}</strong>
                  <span>{card.sub}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="tex-insight-strip" aria-label="Executive insight strip">
        {insightStrip.map((card) => {
          const Icon = STRIP_ICONS[card.iconClass] ?? BarChart3;
          return (
            <article key={card.id} className={`tex-insight-card ${card.iconClass}`.trim()}>
              <div className={`tex-icon-box ${card.iconClass}`.trim()}>
                <Icon size={20} />
              </div>
              <small>{card.label}</small>
              <strong>{card.title}</strong>
              <p>{card.body}</p>
            </article>
          );
        })}
      </section>

      <div className="tex-section-title" id="tex-movement">
        <div>
          <h2>Teacher Workforce Movement</h2>
          <p>
            School-level staffing change distribution and block-wise teacher strength comparison
            make PY versus CY movement immediately visible for district review.
          </p>
        </div>
        <button
          type="button"
          className="tex-section-action"
          onClick={() => onNavigateTab?.("deployment")}
        >
          View deployment risk →
        </button>
      </div>

      <section className="tex-analytics-grid">
        <article className="tex-panel">
          <div className="tex-panel-head">
            <div>
              <h3>Staffing Change Distribution</h3>
              <p>Schools by year-on-year teacher staffing change.</p>
            </div>
            <span className="tex-pill">{donut.total.toLocaleString("en-IN")} schools</span>
          </div>
          <div className="tex-donut-layout">
            <div className="tex-donut-ring" style={donut.style} aria-hidden="true">
              <div className="tex-donut-center">
                <strong>{donut.total.toLocaleString("en-IN")}</strong>
                <span>Schools</span>
              </div>
            </div>
            <div className="tex-donut-legend">
              {donut.segments.map((seg) => (
                <div key={seg.key} className="tex-legend-row">
                  <span className={`tex-legend-dot ${seg.dotClass}`.trim()} />
                  <span>{seg.label}</span>
                  <b>{seg.value.toLocaleString("en-IN")}</b>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="tex-panel">
          <div className="tex-panel-head">
            <div>
              <h3>Block-Wise Teacher Strength</h3>
              <p>Current year versus previous year teacher count by block.</p>
            </div>
            <span className="tex-pill">PY vs CY</span>
          </div>
          <div className="tex-paired-chart" aria-label="Block-wise teacher strength chart">
            {pairedRows.map((row) => (
              <div key={row.block} className="tex-paired-row">
                <div className="tex-paired-label">{row.block}</div>
                <div className="tex-paired-bars">
                  <i style={{ "--h": `${row.pyWidth}%` }} />
                  <i className="current" style={{ "--h": `${row.cyWidth}%` }} />
                </div>
                <div className="tex-paired-value">{row.displayCy}</div>
              </div>
            ))}
          </div>
          <div className="tex-chart-legend">
            <span><i className="legend-swatch" />Current Year</span>
            <span><i className="legend-swatch gray" />Previous Year</span>
          </div>
        </article>
      </section>

      <div className="tex-section-title" id="tex-growth">
        <div>
          <h2>Growth Risk and Executive Actions</h2>
          <p>
            The diverging growth chart makes negative blocks immediately visible. The action queue
            sits beside the chart so review meetings can move directly from insight to intervention.
          </p>
        </div>
        <button
          type="button"
          className="tex-section-action"
          onClick={() => onNavigateTab?.("drilldown")}
        >
          Open school drilldown →
        </button>
      </div>

      <section className="tex-two-column">
        <article className="tex-panel">
          <div className="tex-panel-head">
            <div>
              <h3>Block-Wise Teacher Growth Rate</h3>
              <p>Green blocks are growing; red blocks require deployment review.</p>
            </div>
            <span className="tex-pill">% YoY growth</span>
          </div>
          <div className="tex-growth-chart" aria-label="Block-wise teacher growth rate">
            {growthRows.map((row) => (
              <div key={row.block} className="tex-growth-row">
                <div className="tex-growth-label">{row.block}</div>
                <div className="tex-growth-track">
                  <div
                    className={`tex-growth-bar ${row.positive ? "" : "neg"}`.trim()}
                    style={{ "--w": `${row.width}%` }}
                  />
                </div>
                <div className="tex-growth-value">{row.display}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="tex-panel">
          <div className="tex-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Focused actions for the next district review.</p>
            </div>
            <span className="tex-pill">Priority</span>
          </div>
          <div className="tex-action-card">
            {actionQueue.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? CheckCircle2;
              return (
                <div key={item.title} className="tex-action-item">
                  <div className={`tex-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="tex-action-footer">
            <b>Next review recommendation</b>
            <span>{nextReview}</span>
          </div>
        </article>
      </section>

      <section className="tex-panel tex-table-card" id="tex-watchlist">
        <div className="tex-table-head">
          <div>
            <h3>Block Watchlist</h3>
            <p>Compact executive table for identifying where leadership attention is needed.</p>
          </div>
          <button type="button" className="tex-pill-btn" onClick={() => scrollToId("tex-growth")}>
            Review queue
          </button>
        </div>
        <table className="tex-watch-table" aria-label="Block watchlist table">
          <thead>
            <tr>
              <th>Block</th>
              <th>Teacher Strength</th>
              <th>Growth</th>
              <th>Avg / School</th>
              <th>TQI Status</th>
              <th>Risk</th>
              <th>Recommended Follow-up</th>
            </tr>
          </thead>
          <tbody>
            {watchlist.map((row) => (
              <tr key={row.block}>
                <td>{row.block}</td>
                <td className="score">{row.strength}</td>
                <td className={row.growthPositive ? "positive" : "negative"}>{row.growth}</td>
                <td>{row.avgSchool}</td>
                <td>
                  <span className={`tex-tag ${row.tqiClass}`.trim()}>{row.tqiStatus}</span>
                </td>
                <td>{row.risk}</td>
                <td>{row.followUp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default TeacherExecutiveTab;
